/**
 * Shared image upload for the Smart CMS.
 *
 * NETWORK BOUNDARY — read before changing this.
 *
 * Entity data goes to Postgres; file bytes cannot, so they go to a Supabase
 * storage bucket instead. Images could never have lived in localStorage
 * anyway -- a handful exhausts the ~5MB quota, and object URLs die on
 * reload.
 *
 * What crosses the boundary is deliberately narrow:
 *   - ONLY file bytes are uploaded. No entity data, no store, no history.
 *   - What comes back is a plain public URL string.
 *   - Nothing is ever read back from Supabase to populate a store.
 *
 * Extracted from ImageField so the article body editor and any future caller
 * share one implementation — the validation, naming and error mapping are the
 * parts worth not duplicating.
 */

import { ulid } from "ulid"
import { supabase } from "@/lib/agents/db"

/**
 * Bucket for Smart CMS uploads, separate from the flow editor's "static" so
 * this content is easy to identify and purge.
 *
 * Lowercase with hyphens: bucket names appear directly in storage URLs, where
 * mixed case is a portability hazard.
 *
 * SETUP: must exist and be public-read before uploads work. See
 * the Smart CMS database migration (it creates the bucket).
 */
export const SMART_CMS_BUCKET = "smart-cms-content"

// The open team's folder in the bucket; set by the Smart CMS page
let teamFolder = ""
export function setUploadTeam(team: string) { teamFolder = team.replace(/[^\w-]/g, "") }
export const uploadFolder = () => teamFolder

/** 10MB. Large enough for a real header image, small enough to catch mistakes. */
const MAX_IMAGE_BYTES = 10 * 1024 * 1024

export const IMAGE_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/svg+xml"

export interface UploadResult {
  ok: boolean
  /** Set when ok. */
  url?: string
  /** Set when not ok. */
  error?: string
}

/** True for a URL pointing at our own bucket, i.e. something we uploaded. */
export function isOwnBucketUrl(v: string): boolean {
  return v.includes(`/storage/v1/object/public/${SMART_CMS_BUCKET}/`)
}

/**
 * Validates and uploads one image, returning its public URL.
 *
 * Never throws: callers are UI handlers, and a failed upload should surface a
 * message rather than break a render.
 */
export async function uploadImage(file: File): Promise<UploadResult> {
  if (!file.type.startsWith("image/")) {
    return { ok: false, error: `"${file.name}" is not an image.` }
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return {
      ok: false,
      error: `"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)}MB; the limit is 10MB.`,
    }
  }

  try {
    // ULID prefix keeps names unique and sortable; the original name follows so
    // an uploaded file stays recognisable in the bucket.
    const safeName = file.name.replace(/[^\w.\-]+/g, "_")
    const path = `${teamFolder ? `${teamFolder}/` : ""}${ulid()}-${safeName}`

    const { data, error } = await supabase
      .storage.from(SMART_CMS_BUCKET)
      .upload(path, file, { cacheControl: "3600", upsert: false })

    if (error) throw error
    if (!data?.path) throw new Error("Upload returned no path.")

    const { data: urlData } = supabase.storage.from(SMART_CMS_BUCKET).getPublicUrl(data.path)
    if (!urlData?.publicUrl) throw new Error("Could not resolve a public URL for the upload.")

    return { ok: true, url: urlData.publicUrl }
  } catch (e: any) {
    // Surface the real reason: a missing bucket and a denied policy are the two
    // likely first-run failures and they need different fixes.
    const msg = e?.message || String(e)
    if (/bucket not found/i.test(msg)) {
      return {
        ok: false,
        error: `Bucket "${SMART_CMS_BUCKET}" does not exist yet — it is created by the Smart CMS database migration.`,
      }
    }
    if (/row-level security|policy|unauthorized/i.test(msg)) {
      return {
        ok: false,
        error: `Upload denied by storage policy on "${SMART_CMS_BUCKET}". Check its insert policy.`,
      }
    }
    return { ok: false, error: `Upload failed: ${msg}` }
  }
}
